import { useEffect, useState } from "react";
import {
  LogOut, Plus, Pencil, Trash2, Loader2, X, LayoutDashboard, Receipt, Tag,
} from "lucide-react";
import { toast } from "sonner";
import { api, fmtBRL, parseBRL } from "../lib/api";

const inputCls =
  "w-full bg-ink border border-line-dark px-4 py-3 text-paper text-sm outline-none focus:border-crimson-bright transition-colors placeholder:text-paper/30";

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-ink/80 backdrop-blur-sm" data-testid={testid}>
      <div className="w-full max-w-2xl max-h-[88vh] overflow-y-auto bg-ink-2 border border-line-dark p-8">
        <div className="flex items-center justify-between mb-7">
          <h3 className="font-display font-bold text-2xl text-paper">{title}</h3>
          <button data-testid={`${testid}-close`} onClick={onClose} className="text-paper/50 hover:text-paper">
            <X size={22} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ServiceForm({ initial, onSave, onClose, saving }) {
  const [f, setF] = useState({
    name: initial?.name || "",
    category: initial?.category || "Serviços",
    description: initial?.description || "",
    price: initial?.price ? String(initial.price).replace(".", ",") : "",
    price_label: initial?.price_label || "Sob consulta",
    highlight: initial?.highlight || false,
    active: initial?.active !== false,
    sort_order: initial?.sort_order || 0,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = (e) => {
    e.preventDefault();
    onSave({ ...f, price: parseBRL(f.price), sort_order: Number(f.sort_order) || 0 });
  };
  return (
    <Modal title={initial ? "Editar serviço" : "Novo serviço"} onClose={onClose} testid="service-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="service-form">
        <input data-testid="service-input-name" required placeholder="Nome do serviço *" className={inputCls} value={f.name} onChange={set("name")} />
        <div className="grid grid-cols-2 gap-4">
          <input data-testid="service-input-category" placeholder="Categoria" className={inputCls} value={f.category} onChange={set("category")} />
          <input data-testid="service-input-price" placeholder="Preço (ex: 2.500,00)" className={inputCls} value={f.price} onChange={set("price")} />
        </div>
        <textarea data-testid="service-input-description" placeholder="Descrição" rows={3} className={inputCls} value={f.description} onChange={set("description")} />
        <div className="grid grid-cols-3 gap-4">
          <input data-testid="service-input-price-label" placeholder="Rótulo (Sob consulta)" className={inputCls} value={f.price_label || ""} onChange={set("price_label")} />
          <label className="flex items-center gap-2 text-paper/70 text-sm border border-line-dark px-4 cursor-pointer">
            <input type="checkbox" data-testid="service-input-highlight" checked={f.highlight} onChange={set("highlight")} className="accent-[#8F0101]" /> Destaque
          </label>
          <label className="flex items-center gap-2 text-paper/70 text-sm border border-line-dark px-4 cursor-pointer">
            <input type="checkbox" data-testid="service-input-active" checked={f.active} onChange={set("active")} className="accent-[#8F0101]" /> Ativo
          </label>
        </div>
        <button data-testid="service-form-submit" disabled={saving} className="w-full bg-crimson hover:bg-crimson-bright text-paper font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
          Salvar serviço
        </button>
      </form>
    </Modal>
  );
}

function OfferForm({ initial, onSave, onClose, saving }) {
  const [f, setF] = useState({
    title: initial?.title || "",
    subtitle: initial?.subtitle || "",
    badge: initial?.badge || "",
    footnote: initial?.footnote || "",
    active: initial?.active !== false,
    sort_order: initial?.sort_order || 0,
    items: initial?.items?.length ? initial.items : [{ label: "", price: "" }],
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setItem = (i, k, v) => {
    const items = [...f.items];
    items[i] = { ...items[i], [k]: v };
    setF({ ...f, items });
  };
  const submit = (e) => {
    e.preventDefault();
    onSave({ ...f, sort_order: Number(f.sort_order) || 0, items: f.items.filter((i) => i.label) });
  };
  return (
    <Modal title={initial ? "Editar oferta" : "Nova oferta"} onClose={onClose} testid="offer-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="offer-form">
        <input data-testid="offer-input-title" required placeholder="Título *" className={inputCls} value={f.title} onChange={set("title")} />
        <div className="grid grid-cols-2 gap-4">
          <input data-testid="offer-input-badge" placeholder="Selo (ex: Oferta de outubro)" className={inputCls} value={f.badge} onChange={set("badge")} />
          <input data-testid="offer-input-sort" type="number" placeholder="Ordem" className={inputCls} value={f.sort_order} onChange={set("sort_order")} />
        </div>
        <input data-testid="offer-input-subtitle" placeholder="Subtítulo" className={inputCls} value={f.subtitle} onChange={set("subtitle")} />
        <div className="space-y-2">
          {f.items.map((item, i) => (
            <div key={i} className="flex gap-2">
              <input data-testid={`offer-input-item-label-${i}`} placeholder="Item (ex: LTDA)" className={inputCls} value={item.label} onChange={(e) => setItem(i, "label", e.target.value)} />
              <input data-testid={`offer-input-item-price-${i}`} placeholder="R$ 0,00" className={inputCls} value={item.price} onChange={(e) => setItem(i, "price", e.target.value)} />
              <button type="button" data-testid={`offer-item-remove-${i}`} onClick={() => setF({ ...f, items: f.items.filter((_, x) => x !== i) })} className="text-paper/40 hover:text-crimson-bright px-2">
                <Trash2 size={18} />
              </button>
            </div>
          ))}
          <button type="button" data-testid="offer-add-item" onClick={() => setF({ ...f, items: [...f.items, { label: "", price: "" }] })} className="text-sm text-paper/60 hover:text-paper flex items-center gap-1">
            <Plus size={14} /> Adicionar item
          </button>
        </div>
        <input data-testid="offer-input-footnote" placeholder="Nota de rodapé" className={inputCls} value={f.footnote} onChange={set("footnote")} />
        <label className="flex items-center gap-2 text-paper/70 text-sm border border-line-dark px-4 py-3 cursor-pointer">
          <input type="checkbox" data-testid="offer-input-active" checked={f.active} onChange={set("active")} className="accent-[#8F0101]" /> Ativa
        </label>
        <button data-testid="offer-form-submit" disabled={saving} className="w-full bg-crimson hover:bg-crimson-bright text-paper font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
          Salvar oferta
        </button>
      </form>
    </Modal>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("services");
  const [services, setServices] = useState([]);
  const [offers, setOffers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [serviceModal, setServiceModal] = useState(null);
  const [offerModal, setOfferModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const loadAll = () => {
    api.get("/api/admin/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/api/admin/offers").then((r) => setOffers(r.data)).catch(() => {});
    api.get("/api/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
  };

  useEffect(() => {
    api
      .get("/api/auth/me")
      .then((r) => {
        setUser(r.data);
        loadAll();
      })
      .catch(() => setUser(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await api.post("/api/auth/login", loginForm);
      setUser(data);
      loadAll();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setLoginError(typeof d === "string" ? d : "E-mail ou senha inválidos");
    }
    setLoggingIn(false);
  };

  const logout = async () => {
    await api.post("/api/auth/logout").catch(() => {});
    setUser(false);
  };

  const saveService = async (data) => {
    setSaving(true);
    try {
      if (serviceModal?._id) await api.put(`/api/admin/services/${serviceModal._id}`, data);
      else await api.post("/api/admin/services", data);
      toast.success("Serviço salvo!");
      setServiceModal(null);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    }
    setSaving(false);
  };

  const saveOffer = async (data) => {
    setSaving(true);
    try {
      if (offerModal?._id) await api.put(`/api/admin/offers/${offerModal._id}`, data);
      else await api.post("/api/admin/offers", data);
      toast.success("Oferta salva!");
      setOfferModal(null);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    }
    setSaving(false);
  };

  const removeService = async (s) => {
    if (!window.confirm(`Excluir "${s.name}"?`)) return;
    await api.delete(`/api/admin/services/${s._id}`);
    toast.success("Serviço excluído");
    loadAll();
  };

  const removeOffer = async (o) => {
    if (!window.confirm(`Excluir a oferta "${o.title}"?`)) return;
    await api.delete(`/api/admin/offers/${o._id}`);
    toast.success("Oferta excluída");
    loadAll();
  };

  if (user === null) {
    return (
      <div data-testid="admin-loading" className="min-h-screen bg-ink text-paper flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-crimson-bright" />
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-ink text-paper flex items-center justify-center p-5 grain relative overflow-hidden">
        <div className="absolute inset-0 ledger-bg" />
        <div className="relative w-full max-w-md border border-line-dark bg-ink-2/80 backdrop-blur p-9">
          <img src="/brand/logo-white.png" alt="Contabiltec" className="h-11 w-auto" />
          <h1 className="mt-7 font-display font-extrabold text-3xl tracking-tightest">Painel administrativo</h1>
          <p className="mt-2 text-sm text-paper/50">Atualize preços, ative ou crie serviços e ofertas.</p>
          <form onSubmit={login} className="mt-8 space-y-4">
            <input
              data-testid="admin-login-email"
              type="email"
              required
              placeholder="E-mail"
              className={inputCls}
              value={loginForm.email}
              onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
            />
            <input
              data-testid="admin-login-password"
              type="password"
              required
              placeholder="Senha"
              className={inputCls}
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
            />
            {loginError && (
              <p data-testid="admin-login-error" className="text-sm text-crimson-bright">
                {loginError}
              </p>
            )}
            <button data-testid="admin-login-submit" disabled={loggingIn} className="w-full bg-crimson hover:bg-crimson-bright text-paper font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-50">
              {loggingIn ? <Loader2 size={18} className="animate-spin" /> : "Entrar"}
            </button>
          </form>
          <a href="/" data-testid="admin-back-to-site" className="mt-6 text-sm text-paper/40 hover:text-paper/80 inline-flex items-center gap-1">
            <X size={14} /> Voltar ao site
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink text-paper">
      <header className="border-b border-line-dark sticky top-0 bg-ink/90 backdrop-blur-xl z-40">
        <div className="max-w-6xl mx-auto px-5 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/brand/logo-white.png" alt="Contabiltec" className="h-9 w-auto" />
            <span className="hidden sm:block text-xs uppercase tracking-[0.2em] text-paper/40">Painel</span>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 text-sm text-paper/60 hover:text-paper">
            <LogOut size={16} /> Sair
          </button>
        </div>
        <div className="max-w-6xl mx-auto px-5 flex gap-1 -mt-px">
          {[
            { id: "services", label: "Serviços", icon: Tag },
            { id: "offers", label: "Ofertas", icon: LayoutDashboard },
            { id: "payments", label: "Pagamentos", icon: Receipt },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? "border-crimson-bright text-paper" : "border-transparent text-paper/40 hover:text-paper/70"
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10">
        {tab === "services" && (
          <div data-testid="admin-services-panel">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-2xl">Serviços ({services.length})</h2>
              <button data-testid="admin-add-service" onClick={() => setServiceModal({})} className="inline-flex items-center gap-2 bg-crimson hover:bg-crimson-bright text-paper font-semibold px-5 py-2.5 rounded-full text-sm">
                <Plus size={16} /> Novo serviço
              </button>
            </div>
            <div className="mt-7 grid gap-3">
              {services.map((s) => (
                <div key={s._id} data-testid={`admin-service-row-${s._id}`} className={`border border-line-dark p-5 flex flex-wrap items-center gap-4 ${s.active ? "" : "opacity-40"}`}>
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-display font-bold text-lg">
                      {s.name}{" "}
                      {s.highlight && <span className="text-[10px] uppercase tracking-widest text-crimson-bright align-middle">· destaque</span>}
                    </p>
                    <p className="text-sm text-paper/45">{s.category}</p>
                  </div>
                  <p className="font-display font-bold text-xl w-32 text-right">
                    {s.price ? fmtBRL(s.price) : s.price_label || "Sob consulta"}
                  </p>
                  <div className="flex gap-2">
                    <button data-testid={`admin-edit-service-${s._id}`} onClick={() => setServiceModal(s)} className="border border-line-dark hover:border-paper/60 p-2.5 rounded-lg"><Pencil size={16} /></button>
                    <button data-testid={`admin-delete-service-${s._id}`} onClick={() => removeService(s)} className="border border-line-dark hover:border-crimson-bright hover:text-crimson-bright p-2.5 rounded-lg"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "offers" && (
          <div data-testid="admin-offers-panel">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-2xl">Ofertas ({offers.length})</h2>
              <button data-testid="admin-add-offer" onClick={() => setOfferModal({})} className="inline-flex items-center gap-2 bg-crimson hover:bg-crimson-bright text-paper font-semibold px-5 py-2.5 rounded-full text-sm">
                <Plus size={16} /> Nova oferta
              </button>
            </div>
            <div className="mt-7 grid gap-3">
              {offers.map((o) => (
                <div key={o._id} data-testid={`admin-offer-row-${o._id}`} className={`border border-line-dark p-5 flex flex-wrap items-center gap-4 ${o.active ? "" : "opacity-40"}`}>
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-display font-bold text-lg">{o.title}</p>
                    <p className="text-sm text-paper/45">{o.badge} · {o.items.map((i) => i.price).join(" / ")}</p>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`admin-edit-offer-${o._id}`} onClick={() => setOfferModal(o)} className="border border-line-dark hover:border-paper/60 p-2.5 rounded-lg"><Pencil size={16} /></button>
                    <button data-testid={`admin-delete-offer-${o._id}`} onClick={() => removeOffer(o)} className="border border-line-dark hover:border-crimson-bright hover:text-crimson-bright p-2.5 rounded-lg"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "payments" && (
          <div data-testid="admin-payments-panel">
            <h2 className="font-display font-bold text-2xl">Pagamentos</h2>
            <div className="mt-7 overflow-x-auto border border-line-dark">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line-dark text-paper/45 uppercase text-[11px] tracking-widest">
                    <th className="text-left px-5 py-4">Data</th>
                    <th className="text-left px-5 py-4">Serviço</th>
                    <th className="text-right px-5 py-4">Valor</th>
                    <th className="text-left px-5 py-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-5 py-8 text-center text-paper/35">
                        Nenhum pagamento ainda.
                      </td>
                    </tr>
                  )}
                  {payments.map((p) => (
                    <tr key={p.id} data-testid={`admin-payment-row-${p.id}`} className="border-b border-line-dark/50">
                      <td className="px-5 py-4 text-paper/60">{p.created_at ? new Date(p.created_at).toLocaleString("pt-BR") : "—"}</td>
                      <td className="px-5 py-4 font-medium">{p.service_name}</td>
                      <td className="px-5 py-4 text-right font-display font-bold">{p.amount ? fmtBRL(p.amount) : "—"}</td>
                      <td className="px-5 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${p.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-400" : "bg-paper/10 text-paper/60"}`}>
                          {p.payment_status === "paid" ? "Pago" : p.payment_status === "pending" ? "Pendente" : p.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {serviceModal && <ServiceForm initial={serviceModal._id ? serviceModal : null} onSave={saveService} onClose={() => setServiceModal(null)} saving={saving} />}
      {offerModal && <OfferForm initial={offerModal._id ? offerModal : null} onSave={saveOffer} onClose={() => setOfferModal(null)} saving={saving} />}
    </div>
  );
}
